/**
 * The Page Module shell (piece 12): a Title Banner, a Content region and a
 * conditional Footer. The banner carries the title, the subtitle, the record
 * number and the edit icon, and right-aligned it carries either the status Pill
 * or a list page's primary New button — 🔨 never both.
 */

export class PageModuleError extends Error {}

/** The header is 42px tall, fixed and non-scrolling; the banner's title is 21px and its subtitle 12px. */
export const HEADER_HEIGHT_PX = 42;
export const LOGO_EXTRA_TAP_PX = 8;
export const NAV_ROW_HEIGHT_PX = 30;
export const NAV_EXPANDED_WIDTH_PX = 240;
export const NAV_RAIL_WIDTH_PX = 56;

/**
 * 🔨 The header renders above every other module, including a modal's backdrop,
 * and below the modal itself. 💰 The plan states no z-index anywhere, so this
 * stacking is a stated requirement; left unstated, the framework's order wins.
 */
export const HEADER_LAYER = { aboveBackdrop: true, belowModal: true } as const;

export type BannerRight = 'status' | 'newRecord' | 'none';

export interface BannerShape {
  title: string;
  subtitle?: string | null;
  recordNumber?: string | null;
  editable: boolean;
  right: BannerRight;
}

/** A banner carrying a status and a New button at once is refused, not rendered with both. */
export function assertBannerShape(shape: { hasStatus: boolean; hasNewAction: boolean }): BannerRight {
  if (shape.hasStatus && shape.hasNewAction) throw new PageModuleError('the banner carries the status pill or the New button, never both');
  if (shape.hasStatus) return 'status';
  return shape.hasNewAction ? 'newRecord' : 'none';
}

/**
 * 🔒 The Footer is visible when the page passes footer content, and hidden when it
 * passes none (T113). The product owner, 2026-09-20, reading a page whose footer
 * was missing: the footer belongs on the floor of the viewport with the page
 * short above it.
 *
 * 💰 The test used to be the presence of a status pill, and it was wrong twice
 * over. A statement, a journal and the bank pages carry no status and had their
 * footers hidden with the controls inside them; and with Edit in every accounting
 * document's footer (T168) a status-less document would have offered no way to
 * edit it at all. A page that passes nothing still gets no footer, which is the
 * only rule that was ever actually wanted.
 *
 * 🔨 A status pill is not the test and neither is a pending edit. Both were
 * proxies for *is there anything down there*, and the page itself is what knows.
 */
export function footerVisible(options: { hasFooterContent: boolean }): boolean {
  return options.hasFooterContent;
}

/**
 * The Record Page's own footer: Save Changes as a split Primary, Cancel, and a
 * destructive-outlined Delete at the far left — shown only when editing an
 * existing record, and never on create.
 */
export interface RecordFooterShape {
  primary: 'Save Changes';
  primaryIsSplit: true;
  cancel: 'Cancel';
  showsDelete: boolean;
}

/** 🔨 The Item page carries no Delete button, the second exception to the rule that every Record Page has one. */
export const NO_DELETE_ENTITIES = ['Item'] as const;

export function recordFooter(options: { isExistingRecord: boolean; entity?: string }): RecordFooterShape {
  const forbidden = options.entity != null && (NO_DELETE_ENTITIES as readonly string[]).includes(options.entity);
  return { primary: 'Save Changes', primaryIsSplit: true, cancel: 'Cancel', showsDelete: options.isExistingRecord && !forbidden };
}

/**
 * A core record ends in one of four shapes, and only the first lands in the
 * Trash Bin. Naming them here is what keeps a later piece from adding a second,
 * parallel delete mechanism.
 */
export type EndingShape = 'softDelete' | 'endDate' | 'deactivate' | 'discontinue';

export const RECORD_ENDINGS: Readonly<Record<string, EndingShape>> = {
  CompanyRecord: 'softDelete',
  PersonRecord: 'softDelete',
  LocationRecord: 'softDelete',
  EmploymentRecord: 'endDate',
  SiteRecord: 'endDate',
  UserRecord: 'deactivate',
  Item: 'discontinue',
};

export function inTrashBin(entity: string): boolean {
  return RECORD_ENDINGS[entity] === 'softDelete';
}

/**
 * The delete dialog names the record and counts what is linked to it, never a
 * generic warning. 💰 The verb and the counts must agree with what the cascade
 * does: the Company is soft-deleted and recoverable, while the employments and
 * sites it names are end-dated and are not.
 */
export interface CascadeCounts {
  sites: number;
  employments: number;
  openDeals: number;
}

export function deleteConfirmationText(entity: string, name: string, counts: CascadeCounts): string {
  const parts = [`${counts.sites} Sites`, `${counts.employments} Employments`, `${counts.openDeals} open Deals`];
  return `Delete ${entity} '${name}'? This also removes access to ${parts.join(', ')}.`;
}

/** The restore screen says so before the click, because the cascade is not reversed with the record. */
export const RESTORE_NOTE = 'Employments and sites end-dated by the delete stay ended; reopen each one separately if that was not intended.';

/**
 * The fallback states, each rendered inside the shell rather than as a redirect
 * or a blank page. 🔨 Access Denied withholds the breadcrumb and the record name,
 * so it never leaks the content it refuses to show.
 */
export const FALLBACK_STATES = ['accessDenied', 'notFound', 'error', 'offline'] as const;
export type FallbackState = (typeof FALLBACK_STATES)[number];

export function accessDeniedLeaks(body: string, recordName: string): boolean {
  return recordName.length > 0 && body.includes(recordName);
}

/**
 * 🔒 A record page's title bar folds on a phone whenever it stacks, and folded
 * is the default there (piece 13; decision 1472, amending T341's height rule).
 * The product owner, 2026-10-01: *"the rule should be any time it stacks it lets
 * you shink and defaults to condensed title bar"*. Under the md breakpoint the
 * kind line, the pills, the record number, the controls and the summary column
 * drop beneath the name, so a bar carrying any of them stacks. Folded, it keeps
 * its first line — the picture, the name and the status pill — and a chevron
 * that opens it.
 *
 * 💰 Until 2026-10-01 the bar folded only once it stood taller than a third of
 * the page's height, and a Deal's bar at a quarter of a phone's frame drew whole
 * over six rows with no chevron (T734).
 */

/** Where this browser remembers the viewer's choice: a reading preference on the screen in front of them, never a Settings key. */
export const TITLE_BAR_COLLAPSED_KEY = 'bms2.titleBarCollapsed';

export type TitleBarPreference = 'collapsed' | 'expanded';

/**
 * Whether the title bar draws folded. 🔨 On a phone alone, and only a bar with
 * something beneath its first line to fold; folded by default there. A bar of
 * one line draws whole, and the desktop banner never folds.
 */
export function titleBarCollapsed(state: { phone: boolean; foldable: boolean; preference: TitleBarPreference | null }): boolean {
  return state.phone && state.foldable && state.preference !== 'expanded';
}

/** A stored value read back, anything but the two words reading as no choice made. */
export function titleBarPreferenceOf(stored: string | null | undefined): TitleBarPreference | null {
  return stored === 'collapsed' || stored === 'expanded' ? stored : null;
}

/**
 * 🔒 The avatar menu closes by itself ten seconds after it opens with no pointer
 * or key inside it, and a pointer, a key or a focus inside it restarts the ten
 * seconds (piece 12; T353). The product owner, 2026-09-26: *"it should auto close
 * after 10 seconds"*.
 */
export const AVATAR_MENU_IDLE_MS = 10_000;
